import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Briefcase, Calendar, RefreshCcw, ArrowRight, Zap, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_TONE = {
  central_govt: "bg-blue-500/15 text-blue-200 border-blue-400/30",
  state_govt:   "bg-emerald-500/15 text-emerald-200 border-emerald-400/30",
  psu:          "bg-purple-500/15 text-purple-200 border-purple-400/30",
  bank:         "bg-amber-500/15 text-amber-200 border-amber-400/30",
  railway:      "bg-rose-500/15 text-rose-200 border-rose-400/30",
  other:        "bg-white/10 text-white/70 border-white/20",
};

function daysLeft(iso) {
  if (!iso) return null;
  try {
    const diff = Math.ceil((new Date(iso) - new Date()) / (24 * 3600 * 1000));
    return diff;
  } catch { return null; }
}

function formatRelative(iso) {
  if (!iso) return "";
  const diffM = Math.max(0, Math.floor((new Date() - new Date(iso)) / 60000));
  if (diffM < 1) return "just now";
  if (diffM < 60) return `${diffM}m ago`;
  const h = Math.floor(diffM / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

export default function LatestJobsColumn() {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const waNumber = (process.env.REACT_APP_WHATSAPP_NUMBER || "918055592955").replace(/\D/g, "");

  useEffect(() => {
    let alive = true;
    fetch(`${API}/jobs/latest?limit=20`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => {
        if (!alive) return;
        const items = d.items || [];
        setJobs(items);
        const maxTs = items.reduce((acc, j) => {
          const t = j.scraped_at || j.created_at || "";
          return t > acc ? t : acc;
        }, "");
        setLastUpdated(maxTs || null);
        setLoading(false);
      })
      .catch((e) => { if (alive) { setError(e.message); setLoading(false); } });
    return () => { alive = false; };
  }, []);

  return (
    <aside
      className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-4 sm:p-5 flex flex-col h-full min-h-[520px]"
      data-testid="latest-jobs-column"
    >
      {/* header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-amber-500/15 border border-amber-400/30 text-amber-300 shrink-0">
            <Briefcase className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <h2 className="text-base font-bold leading-tight truncate" data-testid="latest-jobs-title">
              {t("home_dashboard.jobs.title")}
            </h2>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/50">
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 px-1.5 py-0.5 text-emerald-300 font-bold">
                <Zap className="h-2.5 w-2.5" /> LIVE
              </span>
              <RefreshCcw className="h-3 w-3 opacity-60" />
              <span className="text-[10px] normal-case" data-testid="jobs-last-updated">
                {lastUpdated ? formatRelative(lastUpdated) : t("home_dashboard.jobs.updating")}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* list */}
      <div className="flex-1 overflow-y-auto pr-1 space-y-2 max-h-[460px] custom-scrollbar" data-testid="jobs-list">
        {loading && (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-14 rounded-lg bg-white/[0.03] border border-white/5 animate-pulse" />
          ))
        )}
        {error && !loading && (
          <div className="text-xs text-rose-200 p-3 rounded-lg border border-rose-500/30 bg-rose-500/10" data-testid="jobs-error">
            {t("home_dashboard.jobs.load_error")}
          </div>
        )}
        {!loading && !error && jobs.length === 0 && (
          <div className="text-xs text-white/50 p-3">{t("home_dashboard.jobs.empty")}</div>
        )}
        {!loading && !error && jobs.map((job) => {
          const cat = job.category || "other";
          const tone = CATEGORY_TONE[cat] || CATEGORY_TONE.other;
          const dLeft = daysLeft(job.last_date);
          const urgent = dLeft !== null && dLeft >= 0 && dLeft <= 7;
          const waText = encodeURIComponent(
            `Hi UID Infotech, I want to apply for this govt job:\n\n*${job.title}*\n${job.source_url || ""}\n\nPlease guide me on the next steps.`
          );
          const href = `https://wa.me/${waNumber}?text=${waText}`;
          return (
            <a
              key={job.external_id}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={`group block rounded-lg border ${job.is_featured ? "border-amber-400/40 bg-amber-500/[0.04]" : "border-white/8 bg-white/[0.02]"} hover:bg-white/[0.06] hover:border-emerald-400/40 transition-colors p-2.5`}
              data-testid={`jobs-item-${job.external_id?.slice(0, 8)}`}
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                    {job.is_featured && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-200" data-testid="job-featured-chip">
                        <Sparkles className="h-2.5 w-2.5" /> PIN
                      </span>
                    )}
                    <span className={`inline-flex items-center gap-1 rounded-full border ${tone} px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide`}>
                      {t(`jobs.categories.${cat}`, { defaultValue: t("jobs.categories.other") })}
                    </span>
                    {job.last_date && (
                      <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold ${
                        urgent
                          ? "bg-rose-500/20 text-rose-200 border border-rose-400/40"
                          : "bg-white/[0.04] text-white/50 border border-white/10"
                      }`}>
                        <Calendar className="h-2.5 w-2.5" />
                        {urgent && dLeft >= 0
                          ? `${dLeft}${t("jobs.days_left_suffix")}`
                          : new Date(job.last_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                      </span>
                    )}
                  </div>
                  <div className="text-[12.5px] font-semibold text-white/90 group-hover:text-white leading-snug line-clamp-2">
                    {job.title}
                  </div>
                  {job.department && (
                    <div className="mt-0.5 text-[10px] text-white/45 truncate">
                      {job.department}
                    </div>
                  )}
                </div>
                <ArrowRight className="h-3.5 w-3.5 text-white/30 group-hover:text-emerald-300 group-hover:translate-x-0.5 transition-all shrink-0 mt-0.5" />
              </div>
            </a>
          );
        })}
      </div>
    </aside>
  );
}
